const EXTERNAL_REFERRER_KEY = 'gw_external_referrer'
const UTM_SOURCE_KEY = 'gw_utm_source'
const UTM_MEDIUM_KEY = 'gw_utm_medium'
const UTM_CAMPAIGN_KEY = 'gw_utm_campaign'
const UTM_TERM_KEY = 'gw_utm_term'
const UTM_CONTENT_KEY = 'gw_utm_content'
const INTERNAL_DOMAINS = ['givewise.ca', 'localhost', '127.0.0.1', 'tail56ef10.ts.net']

/**
 * Checks if a URL is from an internal domain (GiveWise site)
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is from an internal domain
 */
const isInternalDomain = (url) => {
  if (!url) return false
  try {
    const { hostname } = new URL(url)
    return INTERNAL_DOMAINS.some((domain) => hostname.includes(domain))
  } catch {
    return false
  }
}

/**
 * Captures and stores the original external referrer for first-touch attribution.
 * This function should be called on page load to track where users originally came from.
 *
 * Logic:
 * - If we already have an external referrer stored, keep using it (first-touch)
 * - If the current referrer is external, store it
 * - If the current referrer is internal or empty, ignore it
 *
 * @returns {string|null} - The stored external referrer URL, or null if none exists
 */
export const captureExternalReferrer = () => {
  // Check if we already have a stored external referrer
  const storedReferrer = sessionStorage.getItem(EXTERNAL_REFERRER_KEY)
  if (storedReferrer) {
    return storedReferrer
  }

  // Check if current referrer is external
  const currentReferrer = document.referrer
  if (currentReferrer && !isInternalDomain(currentReferrer)) {
    sessionStorage.setItem(EXTERNAL_REFERRER_KEY, currentReferrer)
    return currentReferrer
  }

  return null
}

/**
 * Gets the stored external referrer without updating it.
 * Use this when you need to read the referrer for tracking purposes.
 *
 * @returns {string|null} - The stored external referrer URL, or null if none exists
 */
export const getExternalReferrer = () => sessionStorage.getItem(EXTERNAL_REFERRER_KEY)

/**
 * Clears the stored external referrer.
 * This is typically not needed as sessionStorage clears when the tab closes,
 * but can be useful for testing or explicit session resets.
 */
export const clearExternalReferrer = () => {
  sessionStorage.removeItem(EXTERNAL_REFERRER_KEY)
}

/**
 * Captures and stores UTM parameters from URL for first-touch attribution.
 * This function should be called on page load to capture UTM parameters.
 * If UTM parameters are already stored, they are preserved (first-touch).
 * Only stores new values if the URL contains UTM parameters.
 */
export const captureUtmParameters = () => {
  const urlParams = new URLSearchParams(window.location.search)

  // Check if URL has any UTM parameters
  const hasUtmParams =
    urlParams.has('utm_source') ||
    urlParams.has('utm_medium') ||
    urlParams.has('utm_campaign') ||
    urlParams.has('utm_term') ||
    urlParams.has('utm_content')

  // If no UTM params in URL, return stored values
  if (!hasUtmParams) {
    return {
      utmSource: sessionStorage.getItem(UTM_SOURCE_KEY),
      utmMedium: sessionStorage.getItem(UTM_MEDIUM_KEY),
      utmCampaign: sessionStorage.getItem(UTM_CAMPAIGN_KEY),
      utmTerm: sessionStorage.getItem(UTM_TERM_KEY),
      utmContent: sessionStorage.getItem(UTM_CONTENT_KEY),
    }
  }

  // Check if we already have stored UTM params (first-touch attribution)
  const hasStoredUtm = sessionStorage.getItem(UTM_SOURCE_KEY) !== null

  if (!hasStoredUtm) {
    // Store new UTM parameters
    const utmSource = urlParams.get('utm_source')
    const utmMedium = urlParams.get('utm_medium')
    const utmCampaign = urlParams.get('utm_campaign')
    const utmTerm = urlParams.get('utm_term')
    const utmContent = urlParams.get('utm_content')

    if (utmSource) sessionStorage.setItem(UTM_SOURCE_KEY, utmSource)
    if (utmMedium) sessionStorage.setItem(UTM_MEDIUM_KEY, utmMedium)
    if (utmCampaign) sessionStorage.setItem(UTM_CAMPAIGN_KEY, utmCampaign)
    if (utmTerm) sessionStorage.setItem(UTM_TERM_KEY, utmTerm)
    if (utmContent) sessionStorage.setItem(UTM_CONTENT_KEY, utmContent)
  }

  return {
    utmSource: sessionStorage.getItem(UTM_SOURCE_KEY),
    utmMedium: sessionStorage.getItem(UTM_MEDIUM_KEY),
    utmCampaign: sessionStorage.getItem(UTM_CAMPAIGN_KEY),
    utmTerm: sessionStorage.getItem(UTM_TERM_KEY),
    utmContent: sessionStorage.getItem(UTM_CONTENT_KEY),
  }
}

/**
 * Gets the stored UTM parameters without updating them.
 * Use this when you need to read UTM data for tracking purposes.
 *
 * @returns {Object} - Object containing stored UTM parameters
 */
export const getStoredUtmParameters = () => ({
  utmSource: sessionStorage.getItem(UTM_SOURCE_KEY),
  utmMedium: sessionStorage.getItem(UTM_MEDIUM_KEY),
  utmCampaign: sessionStorage.getItem(UTM_CAMPAIGN_KEY),
  utmTerm: sessionStorage.getItem(UTM_TERM_KEY),
  utmContent: sessionStorage.getItem(UTM_CONTENT_KEY),
})

/**
 * Gets stored tracking data (referrer URL and UTM parameters) without updating them.
 * Returns an object ready to spread into GraphQL mutation variables.
 *
 * @returns {Object} - Tracking data object with all fields set to value or null
 */
export const getTrackingData = () => {
  const utmParams = getStoredUtmParameters()
  return {
    referrerUrl: getExternalReferrer() || null,
    utmSource: utmParams.utmSource || null,
    utmMedium: utmParams.utmMedium || null,
    utmCampaign: utmParams.utmCampaign || null,
    utmTerm: utmParams.utmTerm || null,
    utmContent: utmParams.utmContent || null,
  }
}

/**
 * Captures UTM parameters and external referrer for first-touch attribution.
 * Extracts UTM parameters from URL query string and combines with external referrer.
 *
 * @param {string|null} fallbackReferrer - Optional fallback value to use if no external referrer is found (e.g., charity name)
 * @returns {Object} - Tracking data object with UTM parameters and referrer URL
 * @returns {string|null} returns.referrerUrl - The external referrer URL or fallback value
 * @returns {string|null} returns.utmSource - UTM source parameter
 * @returns {string|null} returns.utmMedium - UTM medium parameter
 * @returns {string|null} returns.utmCampaign - UTM campaign parameter
 * @returns {string|null} returns.utmTerm - UTM term parameter
 * @returns {string|null} returns.utmContent - UTM content parameter
 */
export const captureTrackingData = (fallbackReferrer = null) => {
  const externalReferrer = captureExternalReferrer()
  const utmParams = captureUtmParameters()

  return {
    referrerUrl: externalReferrer || fallbackReferrer || null,
    utmSource: utmParams.utmSource || null,
    utmMedium: utmParams.utmMedium || null,
    utmCampaign: utmParams.utmCampaign || null,
    utmTerm: utmParams.utmTerm || null,
    utmContent: utmParams.utmContent || null,
  }
}
